import { computed, inject, Injectable, signal } from '@angular/core';
import {
  FacilitySubscriptionProduct,
  FacilitySubscriptionStatus,
  isEntitled,
  OpenCycleSubscription
} from '@umss/shared/data-access';
import { FacilityContextService } from './facility-context.service';
import { FacilitySubscriptionService } from './facility-subscription.service';

/**
 * Cached subscription state for the current facility.
 *
 * Several places need to know whether the facility is subscribed — the usage
 * widget on the dashboard and shifts pages, and the profile setup checklist —
 * so the statuses are fetched once here rather than by each consumer.
 */
@Injectable({ providedIn: 'root' })
export class FacilitySubscriptionStateService {
  private readonly facilityContext = inject(FacilityContextService);
  private readonly subscriptionService = inject(FacilitySubscriptionService);

  private readonly statusesSignal = signal<FacilitySubscriptionStatus[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadedSignal = signal(false);

  private readonly usageSignal = signal<OpenCycleSubscription | null>(null);
  private readonly usageLoadingSignal = signal(false);

  /** The facility the cached statuses belong to. */
  private loadedFor: string | null = null;

  readonly statuses = this.statusesSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loaded = this.loadedSignal.asReadonly();

  /**
   * The open cycle behind the usage widget. Null while loading, and for a
   * facility with no subscription — the endpoint 404s until there is one.
   */
  readonly usage = this.usageSignal.asReadonly();
  readonly usageLoading = this.usageLoadingSignal.asReadonly();

  /**
   * Flex Shifts (`TemporaryStaffing` on the wire) gates shift creation and billing,
   * so it alone decides whether the facility counts as subscribed.
   */
  readonly temporaryStaffingActive = computed(() =>
    isEntitled(
      this.statusesSignal().find(
        (status) =>
          status.product === FacilitySubscriptionProduct.TemporaryStaffing
      )
    )
  );

  /** Whether the facility is entitled to post jobs right now. */
  readonly jobPostingsActive = computed(() => {
    const signalsFound = this.statusesSignal().find(
      (status) => status.product === FacilitySubscriptionProduct.JobPostings
    );
    return isEntitled(signalsFound);
  });

  /**
   * Loads the statuses unless they are already cached for this facility.
   * Safe to call from every consumer on init.
   */
  load(force = false): void {
    const facilityId = this.facilityContext.facilityId();
    if (!facilityId) return;

    const cached = this.loadedFor === facilityId && this.loadedSignal();
    if (!force && (cached || this.loadingSignal())) return;

    this.loadingSignal.set(true);

    this.subscriptionService.getSubscription(facilityId).subscribe({
      next: (subscription) => {
        this.statusesSignal.set(subscription?.products ?? []);
        this.loadedFor = facilityId;
        this.loadedSignal.set(true);
        this.loadingSignal.set(false);
      },
      error: () => {
        this.loadedSignal.set(true);
        this.loadingSignal.set(false);
      }
    });
  }

  /**
   * Reads the open cycle. Always fetches — deliberately uncached.
   *
   * Shared so the three pages showing the widget agree and so a shift change
   * can refresh all of them at once. But not cached across navigations: the
   * worked count moves when a shift completes, which happens server-side with
   * nothing here to hook, so arriving on a page is the only chance to catch
   * up. Caching would leave the figure stale until a full reload.
   *
   * There is one caller per page, so this costs a single request per visit —
   * the reason the statuses below are cached is that three consumers can want
   * them at once.
   */
  loadUsage(): void {
    const facilityId = this.facilityContext.facilityId();
    if (!facilityId) return;

    this.usageLoadingSignal.set(true);

    this.subscriptionService.getOpenCycle(facilityId).subscribe({
      next: (usage) => {
        this.usageSignal.set(usage);
        this.usageLoadingSignal.set(false);
      },
      error: () => {
        this.usageSignal.set(null);
        this.usageLoadingSignal.set(false);
      }
    });
  }

  /**
   * Re-reads the cycle after something that changes the count.
   *
   * Named apart from {@link loadUsage} to say why it is being called:
   * scheduling a shift moves the planned count while the user is still on the
   * page, with no navigation to pick it up.
   */
  refreshUsage(): void {
    this.loadUsage();
  }

  /** Applies a status the API has confirmed, keeping every consumer in step. */
  apply(status: FacilitySubscriptionStatus): void {
    this.statusesSignal.update((current) => [
      ...current.filter((existing) => existing.product !== status.product),
      status
    ]);
  }
}
